// safety/incidents-and-injuries/components/tabs/RtwPlanTab.tsx
import React, { useState } from 'react';
import { Check, Pencil, Trash2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { AddButton, FormModal } from '../shared';
import { fmtDate, initials } from '../../utils/incidents-utils';
import type { Incident, RtwRestrictions, RtwHours, EmployeeRef } from '../../../../../types/incident.types';

const DAYS: (keyof RtwHours)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const REST_FIELDS: { key: keyof RtwRestrictions; label: string }[] = [
    { key: 'lifting', label: 'Lifting Limits' },
    { key: 'carrying', label: 'Carrying Limits' },
    { key: 'pushing', label: 'Pushing / Pulling' },
    { key: 'standing', label: 'Standing Restrictions' },
    { key: 'sitting', label: 'Sitting Restrictions' },
    { key: 'driving', label: 'Driving Restrictions' },
    { key: 'other', label: 'Other Restrictions' },
];
const APPROVER_ROLES = ['RTW Coordinator', 'Supervisor / Manager', 'Worker Representative', 'Insurer', 'Other'];

type RtwPlan = Incident['rtwPlan'];

export const RtwPlanTab: React.FC<{
    incident: Incident;
    employees: EmployeeRef[];
    onChange: (rtwPlan: RtwPlan) => void;
    onCommit: (rtwPlan: RtwPlan) => void;
    onApprove: (index: number) => void;
}> = ({ incident: r, employees, onChange, onCommit, onApprove }) => {
    const [approverModal, setApproverModal] = useState(false);
    // Capacity Restrictions are click-to-edit: pencil reveals the input,
    // check button / Enter saves (Escape cancels).
    const [editingKey, setEditingKey] = useState<keyof RtwRestrictions | null>(null);
    const [draft, setDraft] = useState('');
    const p = r.rtwPlan;
    const total = DAYS.reduce((a, k) => a + (+p.hours[k] || 0), 0);

    // Text edits go through onChange (parent debounces the save);
    // structural changes (add/remove approver) go through onCommit (saves immediately).
    const setHour = (key: keyof RtwHours, val: number) =>
        onChange({ ...p, hours: { ...p.hours, [key]: val } });
    const setApprovalNote = (index: number, note: string) =>
        onChange({ ...p, approvals: p.approvals.map((a, j) => (j === index ? { ...a, note } : a)) });
    const removeApprover = (index: number) =>
        onCommit({ ...p, approvals: p.approvals.filter((_, j) => j !== index) });

    const startEdit = (key: keyof RtwRestrictions) => {
        setEditingKey(key);
        setDraft(p.restrictions[key]);
    };
    const saveEdit = () => {
        if (editingKey === null) return;
        onCommit({ ...p, restrictions: { ...p.restrictions, [editingKey]: draft } });
        setEditingKey(null);
    };

    return (
        <>
            <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 mb-5">
                <TriangleAlert size={16} className="text-amber-500 flex-shrink-0" />
                <input
                    value={p.pendingNote ?? ''}
                    onChange={(e) => onChange({ ...p, pendingNote: e.target.value })}
                    placeholder="Add a pending note (e.g., awaiting doctor review)..."
                    className="w-full bg-transparent text-sm text-amber-700 placeholder:text-amber-500/70 border-0 p-0 focus:ring-0"
                />
            </div>

            <h3 className="text-sm font-semibold mb-3">Capacity Restrictions</h3>
            <div className="grid grid-cols-2 gap-3 mb-6">
                {REST_FIELDS.map((f) => (
                    <div key={f.key} className="rounded-lg border border-border p-3">
                        <div className="flex items-start justify-between">
                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{f.label}</div>
                            {editingKey !== f.key && (
                                <button
                                    onClick={() => startEdit(f.key)}
                                    className="text-muted-foreground hover:text-foreground"
                                    title={`Edit ${f.label}`}
                                >
                                    <Pencil size={14} />
                                </button>
                            )}
                        </div>
                        {editingKey === f.key ? (
                            <div className="flex items-center gap-1.5">
                                <input
                                    autoFocus
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') saveEdit();
                                        if (e.key === 'Escape') setEditingKey(null);
                                    }}
                                    placeholder="—"
                                    className="flex-1 h-8 rounded-md border border-border bg-card px-2 text-sm font-medium"
                                />
                                <button
                                    onClick={saveEdit}
                                    className="h-8 w-8 flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 flex-shrink-0"
                                    title="Save"
                                >
                                    <Check size={15} />
                                </button>
                            </div>
                        ) : (
                            <div className="text-sm font-medium min-h-[32px] flex items-center">
                                {p.restrictions[f.key] || '—'}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <h3 className="text-sm font-semibold mb-3">Hours of Work – Week Planner</h3>
            <div className="rounded-lg border border-border overflow-hidden mb-6">
                <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                        <tr>
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
                                <th key={d} className={`px-2 py-2 font-medium text-muted-foreground ${i >= 5 ? 'bg-muted/60' : ''}`}>
                                    {d}
                                </th>
                            ))}
                            <th className="px-2 py-2 font-medium text-muted-foreground">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {DAYS.map((k, i) => (
                                <td key={k} className={`px-2 py-2 ${i >= 5 ? 'bg-muted/40' : ''}`}>
                                    <div className="text-center">
                                        <input
                                            type="number"
                                            min={0}
                                            max={24}
                                            value={p.hours[k]}
                                            onChange={(e) => setHour(k, +e.target.value)}
                                            className="w-12 h-8 text-center rounded-md border border-border text-sm"
                                        />{' '}
                                        <span className="text-xs text-muted-foreground">h</span>
                                    </div>
                                </td>
                            ))}
                            <td className="px-2 py-2 text-center font-semibold">{total}h</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Approvals &amp; Acknowledgements</h3>
                <AddButton label="Add Approver" onClick={() => setApproverModal(true)} />
            </div>
            <div className="space-y-2">
                {p.approvals.length === 0 && <p className="text-sm text-muted-foreground">No approvals configured. Add approvers to activate the RTW plan.</p>}
                {p.approvals.map((a, i) => (
                    <div
                        key={i}
                        className={`flex items-center gap-3 rounded-lg border px-3.5 py-3 ${
                            a.state === 'Approved' ? 'border-emerald-200 bg-emerald-50/60' : 'border-border'
                        }`}
                    >
                        <div
                            className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                                a.state === 'Approved' ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'
                            }`}
                        >
                            {a.state === 'Approved' ? <Check size={16} /> : initials(a.person)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{a.role}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <span className="whitespace-nowrap">{a.person}</span>
                                {a.state === 'Approved' ? (
                                    a.note ? <span className="truncate">· {a.note}</span> : null
                                ) : (
                                    <input
                                        value={a.note ?? ''}
                                        onChange={(e) => setApprovalNote(i, e.target.value)}
                                        placeholder="· add note..."
                                        className="flex-1 min-w-0 bg-transparent border-0 p-0 focus:ring-0 text-xs"
                                    />
                                )}
                            </div>
                        </div>
                        {a.state === 'Approved' ? (
                            <div className="text-right flex-shrink-0">
                                <div className="text-xs text-muted-foreground">{fmtDate(a.date)}</div>
                                <span className="text-xs text-emerald-600 font-medium">Approved</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs text-amber-600 font-medium">Pending</span>
                                <button
                                    onClick={() => onApprove(i)}
                                    className="text-xs font-medium h-8 px-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
                                >
                                    Approve
                                </button>
                                <button
                                    onClick={() => removeApprover(i)}
                                    className="text-muted-foreground hover:text-destructive p-1"
                                    title="Remove approver"
                                >
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {approverModal && (
                <FormModal
                    title="Add Approver"
                    fields={[
                        { key: 'role', label: 'Role', type: 'select', options: APPROVER_ROLES, value: APPROVER_ROLES[0] },
                        {
                            key: 'person',
                            label: 'Person',
                            type: employees.length > 0 ? 'select' : 'text',
                            options: employees.map((e) => e.name),
                            value: employees[0]?.name ?? '',
                        },
                        { key: 'note', label: 'Note (optional)', value: '' },
                    ]}
                    onClose={() => setApproverModal(false)}
                    onSubmit={(v) => {
                        if (!v.person) {
                            toast.error('Person is required');
                            return;
                        }
                        onCommit({
                            ...p,
                            approvals: [...p.approvals, { role: v.role, person: v.person, state: 'Pending', note: v.note || undefined }],
                        });
                        setApproverModal(false);
                    }}
                />
            )}
        </>
    );
};
